import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";
import useStore from "../store/useStore";
import Poster from "../components/Poster";
import { TOAST_MS, FILE_SIZE } from "../lib/constants";

export default function AddShow() {
  const navigate = useNavigate();
  const addShow = useStore((s) => s.addShow);
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [theatre, setTheatre] = useState("");
  const [venue, setVenue] = useState("");
  const [dateLabel, setDateLabel] = useState("");
  const [city, setCity] = useState("toronto");
  const [posterUrl, setPosterUrl] = useState("");
  const [description, setDescription] = useState("");
  const [playwright, setPlaywright] = useState("");
  const [director, setDirector] = useState("");

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_SIZE.MAX_POSTER_BYTES) {
      toast("image too big (max 1.5mb)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPosterUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast("title required");
      return;
    }
    const id = addShow({
      title: title.trim(),
      theatre: theatre.trim(),
      venue: venue.trim() || theatre.trim(),
      company: theatre.trim(),
      dateLabel: dateLabel.trim(),
      city: city.trim().toLowerCase() || "toronto",
      posterUrl: posterUrl || null,
      description: description.trim(),
      playwright: playwright.trim() || null,
      director: director.trim() || null,
      categories: ["custom"],
    });
    toast("added", { duration: 1500 });
    navigate(`/show/${id}`);
  };

  const previewShow = {
    id: "preview",
    title: title || "your show",
    theatre: theatre || "venue",
    posterUrl: posterUrl || null,
  };

  return (
    <main data-testid="add-show-page" className="min-h-screen bg-black text-white pb-[calc(10rem+env(safe-area-inset-bottom))]">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <button
          onClick={() => navigate(-1)}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back
        </button>
        <h1 className="font-display text-4xl leading-none mt-3">
          add a show<span className="text-acid">.</span>
        </h1>
        <p className="font-mono text-[11px] text-white/50 mt-2 leading-relaxed">
          for shows not in the list.
        </p>
      </header>

      <form onSubmit={submit} className="px-5 py-6 space-y-5">
        {/* poster + preview */}
        <div className="flex gap-4 items-start">
          <div className="w-24 shrink-0">
            <Poster show={previewShow} size="hero" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              poster (optional)
            </label>
            <input
              data-testid="add-poster-url"
              value={posterUrl.startsWith("data:") ? "" : posterUrl}
              onChange={(e) => setPosterUrl(e.target.value)}
              placeholder="image url"
              className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs focus:border-acid outline-none"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
              data-testid="add-poster-file"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-mono text-[10px] lowercase border-2 border-white/40 text-white/70 px-2 py-1.5 hover:border-white transition-colors flex items-center gap-1"
              >
                <Upload className="w-3 h-3" strokeWidth={2} />
                upload
              </button>
              {posterUrl && (
                <button
                  type="button"
                  onClick={() => setPosterUrl("")}
                  className="font-mono text-[10px] lowercase border-2 border-white/40 text-white/70 px-2 py-1.5 hover:border-white transition-colors flex items-center gap-1"
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                  clear
                </button>
              )}
            </div>
          </div>
        </div>

        <Field label="title *" testid="add-title" value={title} onChange={setTitle} placeholder="show title" />
        <Field label="theatre / company" testid="add-theatre" value={theatre} onChange={setTheatre} />
        <Field label="venue" testid="add-venue" value={venue} onChange={setVenue} placeholder="if different from theatre" />
        <Field label="date / range" testid="add-date" value={dateLabel} onChange={setDateLabel} placeholder="apr 2026 / apr 7 – may 3, 2026" />
        <Field label="city" testid="add-city" value={city} onChange={setCity} />
        <Field label="playwright / creator" testid="add-playwright" value={playwright} onChange={setPlaywright} />
        <Field label="director" testid="add-director" value={director} onChange={setDirector} />

        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            description
          </label>
          <textarea
            data-testid="add-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="a sentence or two"
            className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none resize-none"
          />
        </div>

        <button
          type="submit"
          data-testid="add-submit"
          className="w-full bg-acid text-black font-mono text-sm uppercase tracking-wider py-3 border-2 border-acid hover:bg-black hover:text-acid transition-colors"
        >
          create show
        </button>
        <p className="font-mono text-[10px] text-white/40 text-center">
          saved locally. you can save, log, and add it to lists.
        </p>
      </form>
    </main>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
      />
    </div>
  );
}
